import { APIcallerv2 } from './APIcaller';

/**
 * Paginated external orders of the caller's client, restricted to the given store perimeter
 * (POST /v2/b2b/external-orders/paginated, COR-1681). Pagination / sort / search travel in the
 * query string; the store perimeter and the filters travel in the body, the latter as drizzle
 * condition descriptors (e.g. `{ inArray: [...] }`, `{ between: [...] }`).
 * Resolves to `{ data, count }`, the count being independent of the pagination.
 */
const getPaginatedExternalOrders = ({ page, limit, search, orderBy, orderType }, body) => {
  const url = `/b2b/external-orders/paginated?page=${page}&limit=${limit}&search=${encodeURIComponent(
    search,
  )}&orderBy=${orderBy}&orderType=${orderType}`;

  return APIcallerv2.apiPost(url, body);
};

/**
 * Soft-delete a draft external order via DELETE /v2/b2b/external-orders/:id (COR-1684). The order
 * leaves the list for good from the user standpoint. Only the draft status is accepted: an order
 * validated in the meantime answers a 409, one already deleted or whose client has been deactivated
 * a 404.
 */
const deleteExternalOrder = (externalOrderId) =>
  APIcallerv2.apiDelete(`/b2b/external-orders/${externalOrderId}`);

/**
 * Create an external order via POST /v2/b2b/external-orders (COR-1682), either as a draft or
 * directly as `to_come` when the draft is validated straight away — the payload carries the target
 * `status`. Resolves to the created order `{ id, reference }`.
 */
const createExternalOrder = (payload) => APIcallerv2.apiPost('/b2b/external-orders', payload);

/**
 * Save a draft external order via PATCH /v2/b2b/external-orders/:id (COR-1710). Every field is
 * optional, an absent one keeping its saved value, and `products` replaces the whole set of lines —
 * the ones left out are removed, the quantities back to 0 dropped. Only the draft status is
 * accepted: an order validated in the meantime answers a 409, one deleted or whose client has been
 * deactivated a 404. Resolves to `{ id, reference, totalWithTaxes }`, the reference having been
 * regenerated if the store or the external client changed.
 */
const updateDraftExternalOrder = (externalOrderId, payload) =>
  APIcallerv2.apiPatch(`/b2b/external-orders/${externalOrderId}`, payload);

/**
 * Validate a draft external order into `A venir` via POST /v2/b2b/external-orders/:id/validate
 * (COR-1685). The route takes no body: it replays the catalog checks on the lines already saved,
 * freezes their price on the BDC and generates the stock events. It answers a 409 on an order that
 * is not a draft anymore, and a 422 when no line carries a quantity above 0.
 */
const validateExternalOrder = (externalOrderId) =>
  APIcallerv2.apiPost(`/b2b/external-orders/${externalOrderId}/validate`);

/**
 * Edit the comment of a `A venir` or `En préparation` order via
 * PATCH /v2/b2b/external-orders/:id/comment (COR-1696). The comment is the only field these statuses
 * let go of, hence a route of its own rather than the draft one. A `Préparée` order is read-only and
 * answers a 409.
 */
const updateExternalOrderComment = (externalOrderId, comment) =>
  APIcallerv2.apiPatch(`/b2b/external-orders/${externalOrderId}/comment`, { comment });

/**
 * Play a status transition via PATCH /v2/b2b/external-orders/:id/status (COR-1686). The target is
 * the status the order must land on, `prepared` standing for both prepared statuses — the compliance
 * is resolved server-side. A pending delivery edition can travel with the transition (COR-1771):
 * `{ deliveryDate, deliveryTime }` is then written with the status, and the stock events follow the
 * date. A transition the flow does not allow, or an order that already left the expected status,
 * answers a 409.
 */
const updateExternalOrderStatus = (externalOrderId, status, delivery) =>
  APIcallerv2.apiPatch(`/b2b/external-orders/${externalOrderId}/status`, {
    status,
    ...(delivery ?? {}),
  });

/**
 * Save the BDL quantities and preparer ticks of an order in preparation via
 * PATCH /v2/b2b/external-orders/:id/prepared-quantities (COR-1697). Each line carries
 * `{ productId, quantity, checked }`, a quantity of 0 being a valid entry — nothing was delivered
 * for that line. The BDC lines are never touched, and the stock events of the order are regenerated
 * server-side on the saved quantities. An order that left the `En préparation` status answers a 409.
 */
const updateExternalOrderPreparedQuantities = (externalOrderId, products) =>
  APIcallerv2.apiPatch(`/b2b/external-orders/${externalOrderId}/prepared-quantities`, { products });

/**
 * Generate the delivery note PDF of a prepared order via
 * POST /v2/b2b/external-orders/:id/delivery-note (COR-1728). Every call regenerates the document,
 * uploads it to the CDN and overwrites the link stored on the order. Resolves to
 * `{ deliveryNoteUrl }`, ready to be downloaded. An order that is not prepared answers a 409.
 */
const generateExternalOrderDeliveryNote = (externalOrderId) =>
  APIcallerv2.apiPost(`/b2b/external-orders/${externalOrderId}/delivery-note`);

/**
 * The relations of an order payload travel as objects, while the form dropdowns resolve their
 * selection against ids: both shapes are handed over.
 */
const flattenExternalOrderRelations = ({ store, externalClient, channel, ...header }) => ({
  ...header,
  store: store ?? null,
  externalClient: externalClient ?? null,
  channel: channel ?? null,
  storeId: store?.id ?? null,
  externalClientId: externalClient?.id ?? null,
  channelId: channel?.id ?? null,
});

/**
 * Detail of an external order via GET /v2/b2b/external-orders/:id (COR-1683). Opening a draft
 * realigns it on the client catalog server-side — the products that left the catalog are dropped,
 * the price and the VAT rate of the others refreshed — so what comes back is what the order is worth
 * right now.
 *
 * The lines are handed over whole — the draft form only reads their quantity, but the later statuses
 * display the price and the VAT rate frozen on them.
 */
const getExternalOrder = async (externalOrderId) => {
  const { externalProductOrders, ...externalOrder } = await APIcallerv2.apiGet(
    `/b2b/external-orders/${externalOrderId}`,
  );

  return {
    ...flattenExternalOrderRelations(externalOrder),
    products: externalProductOrders ?? [],
  };
};

/**
 * Move the delivery date and time of an order past the draft status via
 * PATCH /v2/b2b/external-orders/:id/delivery (COR-1771) — the only edition a `Préparée` order still
 * allows. The stock events of the order are deleted and regenerated server-side when the date
 * changes; a time-only change never touches them. A draft answers a 409: its delivery is edited
 * through the draft update route. The route answers an empty 200 on purpose: a successful save
 * lands back on the list and reads nothing.
 */
const updateExternalOrderDelivery = (externalOrderId, { deliveryDate, deliveryTime }) =>
  APIcallerv2.apiPatch(`/b2b/external-orders/${externalOrderId}/delivery`, {
    deliveryDate,
    deliveryTime,
  });

/**
 * Prefilled order payload of a duplication via GET /v2/b2b/external-orders/:id/duplicate
 * (COR-1691). Nothing is created: the creation form opens on this payload and the order only exists
 * once saved or validated from there. The ordered quantities (BDC) are the duplicated ones whatever
 * the source status, the products that left the client catalog are dropped, and the price and the
 * VAT rate of the kept ones are the current catalog ones rather than the frozen ones.
 */
const getDuplicatedExternalOrderPayload = async (externalOrderId) => {
  const { products, ...externalOrder } = await APIcallerv2.apiGet(
    `/b2b/external-orders/${externalOrderId}/duplicate`,
  );

  return {
    ...flattenExternalOrderRelations(externalOrder),
    products: products ?? [],
  };
};

export const externalOrder = {
  getPaginatedExternalOrders,
  deleteExternalOrder,
  createExternalOrder,
  updateDraftExternalOrder,
  validateExternalOrder,
  updateExternalOrderComment,
  updateExternalOrderDelivery,
  updateExternalOrderStatus,
  updateExternalOrderPreparedQuantities,
  generateExternalOrderDeliveryNote,
  getExternalOrder,
  getDuplicatedExternalOrderPayload,
};
